'use client';

import { useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';

import { useAppVersionCheck } from 'src/hooks/use-app-version-check';
import { useChunkLoadRecovery } from 'src/hooks/use-chunk-load-recovery';

import { isReloadUnsafe } from './is-reload-unsafe';

// ----------------------------------------------------------------------

/**
 * Silently upgrades an open tab to a freshly deployed build.
 *
 * `useAppVersionCheck` detects when a newer build is live (it polls
 * `version.json` and re-checks whenever the tab regains focus). Instead of
 * prompting, we wait for the user's next in-app navigation and hard-reload
 * at the destination, so it loads with the new HTML and chunks.
 *
 * Reloading on navigation — not on tab focus or a timer — is what keeps this
 * safe: the user is already leaving their current page, so a full reload at
 * the page they asked for never discards work they were in the middle of.
 * While they stay on one page, nothing reloads underneath them.
 */
export function AppAutoUpdate() {
  const { updateAvailable } = useAppVersionCheck();

  useChunkLoadRecovery();

  const pathname = usePathname();

  // Act on a pathname change, but read the freshest flag without making it a
  // dependency — a navigation reloads only when an update is already pending.
  const updateAvailableRef = useRef(false);
  updateAvailableRef.current = updateAvailable;

  // The effect also runs on first mount; that is not a navigation.
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    if (updateAvailableRef.current && !isReloadUnsafe()) {
      // The URL has already advanced to the destination, so reloading here
      // fetches that page fresh — new index.html and new hashed chunks.
      // If a reload is unsafe right now (e.g. an allocation is running), we
      // skip it and let a later navigation pick up the update instead.
      window.location.reload();
    }
  }, [pathname]);

  return null;
}
